import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  AccountTree,
  ArrowBack,
  CallSplit,
  CheckCircle,
  ContentCopy,
  Delete,
  DynamicFeed,
  Input,
  Link,
  Message,
  Person,
  PlayArrow,
  Publish,
  Refresh,
  Save,
  StopCircle,
} from "@mui/icons-material";
import ReactFlow, {
  Background,
  Connection,
  Controls,
  Edge,
  MiniMap,
  Node,
  ReactFlowProvider,
} from "reactflow";
import "reactflow/dist/style.css";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardHeading } from "../dashboard/DashboardPage";
import useAuthUser from "../hooks/useAuthUser";
import useSuperAdminAccess from "../hooks/useSuperAdminAccess";
import { getUserSettingsById } from "../../services/settings.service";
import { flowApi, flowApiErrorMessage } from "../../services/flow.service";
import {
  ChatbotConfiguration,
  ConditionBranch,
  FlowConnector,
  FlowDefinition,
  FlowDocument,
  FlowNodeDefinition,
  FlowNodeType,
  FlowOption,
  FlowValidationIssue,
  IntentRoute,
  PreviewResponse,
} from "../../models/flow.model";

const NODE_TYPES: Array<{ type: FlowNodeType; label: string; icon: React.ReactNode; color: string }> = [
  { type: "MESSAGE", label: "Message", icon: <Message />, color: "#196c52" },
  { type: "MENU", label: "Menu", icon: <AccountTree />, color: "#466a7a" },
  { type: "DYNAMIC_MENU", label: "Dynamic Menu", icon: <DynamicFeed />, color: "#557857" },
  { type: "INPUT", label: "Input", icon: <Input />, color: "#915d08" },
  { type: "CONDITION", label: "Condition", icon: <CallSplit />, color: "#a85740" },
  { type: "CONNECTOR_ACTION", label: "Connector", icon: <Link />, color: "#497968" },
  { type: "HUMAN_HANDOFF", label: "Handoff", icon: <Person />, color: "#8a586b" },
  { type: "END", label: "End", icon: <StopCircle />, color: "#65766f" },
];

const nodeColor = (type: FlowNodeType) => NODE_TYPES.find((item) => item.type === type)?.color || "#607d8b";
const newId = (prefix: string) => `${prefix.toLowerCase()}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const starterDefinition = (): FlowDefinition => ({
  startNodeId: "welcome",
  fallbackMessage: "I'm not sure which option you need. Please choose below.",
  settings: { mainMenuNodeId: "main_menu" },
  intents: [],
  nodes: [
    { id: "welcome", type: "MESSAGE", name: "Welcome", message: "Welcome! 👋", nextNodeId: "main_menu", position: { x: 260, y: 40 } },
    {
      id: "main_menu", type: "MENU", name: "Main Menu", message: "How can we help?", position: { x: 260, y: 180 },
      options: [{ id: "finish", title: "Get Started", targetNodeId: "end" }],
    },
    { id: "end", type: "END", name: "End", message: "Thanks for chatting with us.", position: { x: 260, y: 340 } },
  ],
});

const targetsFor = (node: FlowNodeDefinition): Array<{ id: string; label?: string }> => {
  const values: Array<{ id: string; label?: string }> = [];
  if (node.nextNodeId) values.push({ id: node.nextNodeId });
  if (node.errorNodeId) values.push({ id: node.errorNodeId, label: "error" });
  if (node.emptyNodeId) values.push({ id: node.emptyNodeId, label: "empty" });
  (node.options || []).forEach((option) => values.push({ id: option.targetNodeId, label: option.title }));
  (node.branches || []).forEach((branch) => values.push({ id: branch.targetNodeId, label: `${branch.variable} ${branch.operator}` }));
  if (node.defaultNodeId) values.push({ id: node.defaultNodeId, label: "default" });
  if (node.dynamicMapping?.targetNodeId) values.push({ id: node.dynamicMapping.targetNodeId, label: "selection" });
  return values;
};

const messageFor = flowApiErrorMessage;

function NodeTargetSelect({ value, nodes, onChange, label = "Next node" }: {
  value?: string; nodes: FlowNodeDefinition[]; onChange: (value: string) => void; label?: string;
}) {
  return <TextField select fullWidth size="small" label={label} value={value || ""} onChange={(event) => onChange(event.target.value)}>
    <MenuItem value=""><em>None</em></MenuItem>
    {nodes.map((node) => <MenuItem key={node.id} value={node.id}>{node.name} ({node.type})</MenuItem>)}
  </TextField>;
}

function ParameterEditor({ value, onChange }: { value: Record<string, unknown>; onChange: (value: Record<string, unknown>) => void }) {
  const entries = Object.entries(value || {});
  const replace = (index: number, key: string, itemValue: unknown) => {
    const next = entries.map(([currentKey, currentValue], position) => position === index ? [key, itemValue] : [currentKey, currentValue]);
    onChange(Object.fromEntries(next.filter(([entryKey]) => Boolean(entryKey))));
  };
  return <Stack spacing={1}>
    <Typography variant="caption" fontWeight={700}>Action parameters</Typography>
    {entries.map(([key, itemValue], index) => <Stack direction="row" spacing={1} key={`${key}-${index}`}>
      <TextField size="small" label="Parameter" value={key} onChange={(event) => replace(index, event.target.value, itemValue)} />
      <TextField fullWidth size="small" label="Value or {{variable}}" value={String(itemValue ?? "")} onChange={(event) => replace(index, key, event.target.value)} />
      <IconButton aria-label="delete parameter" onClick={() => onChange(Object.fromEntries(entries.filter((_, position) => position !== index)))}><Delete /></IconButton>
    </Stack>)}
    <Button size="small" startIcon={<Add />} onClick={() => onChange({ ...value, [`parameter_${entries.length + 1}`]: "" })}>Add parameter</Button>
  </Stack>;
}

function NodeEditor({ node, nodes, connectors, onChange, onDelete }: {
  node: FlowNodeDefinition;
  nodes: FlowNodeDefinition[];
  connectors: FlowConnector[];
  onChange: (node: FlowNodeDefinition) => void;
  onDelete: () => void;
}) {
  const update = (value: Partial<FlowNodeDefinition>) => onChange({ ...node, ...value });
  const connector = node.connector;
  const selectedConnector = connectors.find((item) => item.connectorId === connector?.connectorId);
  const actionNames = (selectedConnector?.actions || []).map((action) => typeof action === "string" ? action : action.name);

  const addOption = () => update({ options: [...(node.options || []), { id: newId("option"), title: "New option", targetNodeId: "" }] });
  const updateOption = (index: number, value: Partial<FlowOption>) => update({
    options: (node.options || []).map((option, position) => position === index ? { ...option, ...value } : option),
  });
  const removeOption = (index: number) => update({ options: (node.options || []).filter((_, position) => position !== index) });
  const addBranch = () => update({ branches: [...(node.branches || []), { variable: "variables.value", operator: "eq", value: "", targetNodeId: "" }] });
  const updateBranch = (index: number, value: Partial<ConditionBranch>) => update({
    branches: (node.branches || []).map((branch, position) => position === index ? { ...branch, ...value } : branch),
  });

  const showMessage = ["MESSAGE", "MENU", "DYNAMIC_MENU", "INPUT", "HUMAN_HANDOFF", "END"].includes(node.type);
  const showNext = ["MESSAGE", "INPUT", "CONNECTOR_ACTION"].includes(node.type);
  const showConnector = ["DYNAMIC_MENU", "CONNECTOR_ACTION"].includes(node.type);

  return <Stack spacing={2} sx={{ p: 2, minWidth: 0 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Box><Typography variant="overline" color="text.secondary">{node.type}</Typography><Typography variant="h6" fontWeight={700}>Configuration</Typography></Box>
      <Tooltip title="Delete node"><IconButton color="error" onClick={onDelete}><Delete /></IconButton></Tooltip>
    </Stack>
    <TextField label="Node name" size="small" value={node.name} onChange={(event) => update({ name: event.target.value })} />
    {showMessage && <TextField label={node.type === "INPUT" ? "Prompt" : "Message"} multiline minRows={3} value={node.message || ""} onChange={(event) => update({ message: event.target.value })} helperText="Use {{variables.fieldName}} to insert collected values." />}
    {showNext && <NodeTargetSelect nodes={nodes} value={node.nextNodeId} onChange={(nextNodeId) => update({ nextNodeId })} />}

    {node.type === "INPUT" && <>
      <TextField label="Store answer as" size="small" value={node.inputKey || ""} onChange={(event) => update({ inputKey: event.target.value })} />
      <TextField select label="Input validation" size="small" value={node.inputType || "text"} onChange={(event) => update({ inputType: event.target.value as FlowNodeDefinition["inputType"] })}>
        {["text", "number", "email", "date", "phone"].map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
      </TextField>
      <FormControlLabel control={<Switch checked={node.required !== false} onChange={(_, checked) => update({ required: checked })} />} label="Required" />
    </>}

    {node.type === "MENU" && <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography fontWeight={700}>Options</Typography><Button size="small" startIcon={<Add />} onClick={addOption}>Add</Button></Stack>
      {(node.options || []).map((option, index) => <Paper key={option.id} variant="outlined" sx={{ p: 1.5 }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}><TextField fullWidth size="small" label="Button title" value={option.title} onChange={(event) => updateOption(index, { title: event.target.value })} /><IconButton onClick={() => removeOption(index)}><Delete /></IconButton></Stack>
          <TextField size="small" label="Description (lists)" value={option.description || ""} onChange={(event) => updateOption(index, { description: event.target.value })} />
          <NodeTargetSelect nodes={nodes} value={option.targetNodeId} label="Destination" onChange={(targetNodeId) => updateOption(index, { targetNodeId })} />
        </Stack>
      </Paper>)}
    </Stack>}

    {showConnector && <>
      <TextField select size="small" label="Business connector" value={connector?.connectorId || ""} onChange={(event) => {
        const selected = connectors.find((item) => item.connectorId === event.target.value);
        update({ connector: { ...connector, connectorId: selected?.connectorId, connectorType: selected?.type, action: connector?.action || "", parameters: connector?.parameters || {} } });
      }}>
        <MenuItem value=""><em>Select connector</em></MenuItem>
        {connectors.map((item) => <MenuItem key={`${item.connectorId}-${item.type}`} value={item.connectorId}>{item.connectorName} ({item.type})</MenuItem>)}
      </TextField>
      {actionNames.length ? <TextField select size="small" label="Action" value={connector?.action || ""} onChange={(event) => update({ connector: { ...connector!, action: event.target.value } })}>
        {actionNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
      </TextField> : <TextField size="small" label="Connector action" value={connector?.action || ""} onChange={(event) => update({ connector: { ...(connector || { parameters: {} }), action: event.target.value } })} />}
      <TextField size="small" label="Save result as" value={connector?.resultKey || ""} onChange={(event) => update({ connector: { ...(connector || { action: "", parameters: {} }), resultKey: event.target.value } })} />
      <ParameterEditor value={connector?.parameters || {}} onChange={(parameters) => update({ connector: { ...(connector || { action: "" }), parameters } })} />
      <NodeTargetSelect nodes={nodes} value={node.errorNodeId} label="On connector error" onChange={(errorNodeId) => update({ errorNodeId })} />
    </>}

    {node.type === "DYNAMIC_MENU" && <>
      <Divider /><Typography fontWeight={700}>Returned option mapping</Typography>
      <TextField size="small" label="Items path" value={node.dynamicMapping?.itemsPath || ""} onChange={(event) => update({ dynamicMapping: { ...(node.dynamicMapping || { idPath: "id", titlePath: "title", targetNodeId: "" }), itemsPath: event.target.value } })} />
      <TextField size="small" label="Option ID path" value={node.dynamicMapping?.idPath || ""} onChange={(event) => update({ dynamicMapping: { ...(node.dynamicMapping || { itemsPath: "items", titlePath: "title", targetNodeId: "" }), idPath: event.target.value } })} />
      <TextField size="small" label="Title path" value={node.dynamicMapping?.titlePath || ""} onChange={(event) => update({ dynamicMapping: { ...(node.dynamicMapping || { itemsPath: "items", idPath: "id", targetNodeId: "" }), titlePath: event.target.value } })} />
      <TextField size="small" label="Description path" value={node.dynamicMapping?.descriptionPath || ""} onChange={(event) => update({ dynamicMapping: { ...node.dynamicMapping!, descriptionPath: event.target.value } })} />
      <TextField size="small" label="Stored value path (optional)" value={node.dynamicMapping?.valuePath || ""} onChange={(event) => update({ dynamicMapping: { ...node.dynamicMapping!, valuePath: event.target.value } })} />
      <NodeTargetSelect nodes={nodes} value={node.dynamicMapping?.targetNodeId} label="On selection" onChange={(targetNodeId) => update({ dynamicMapping: { ...node.dynamicMapping!, targetNodeId } })} />
      <NodeTargetSelect nodes={nodes} value={node.emptyNodeId} label="When no options" onChange={(emptyNodeId) => update({ emptyNodeId })} />
    </>}

    {node.type === "CONDITION" && <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between"><Typography fontWeight={700}>Branches</Typography><Button size="small" startIcon={<Add />} onClick={addBranch}>Add</Button></Stack>
      {(node.branches || []).map((branch, index) => <Paper key={index} variant="outlined" sx={{ p: 1.5 }}><Stack spacing={1}>
        <Stack direction="row" spacing={1}><TextField fullWidth size="small" label="Variable path" value={branch.variable} onChange={(event) => updateBranch(index, { variable: event.target.value })} /><IconButton onClick={() => update({ branches: (node.branches || []).filter((_, position) => position !== index) })}><Delete /></IconButton></Stack>
        <TextField select size="small" label="Operator" value={branch.operator} onChange={(event) => updateBranch(index, { operator: event.target.value as ConditionBranch["operator"] })}>{["eq", "ne", "gt", "gte", "lt", "lte", "in", "contains", "exists"].map((operator) => <MenuItem key={operator} value={operator}>{operator}</MenuItem>)}</TextField>
        <TextField size="small" label="Compare value" value={String(branch.value ?? "")} onChange={(event) => updateBranch(index, { value: event.target.value })} />
        <NodeTargetSelect nodes={nodes} value={branch.targetNodeId} label="Destination" onChange={(targetNodeId) => updateBranch(index, { targetNodeId })} />
      </Stack></Paper>)}
      <NodeTargetSelect nodes={nodes} value={node.defaultNodeId} label="Default destination" onChange={(defaultNodeId) => update({ defaultNodeId })} />
    </Stack>}

    {node.type === "HUMAN_HANDOFF" && <TextField size="small" label="Handoff reason" value={node.handoffReason || ""} onChange={(event) => update({ handoffReason: event.target.value })} />}
  </Stack>;
}

function IntentsEditor({ intents, nodes, onChange }: { intents: IntentRoute[]; nodes: FlowNodeDefinition[]; onChange: (value: IntentRoute[]) => void }) {
  return <Stack spacing={1.5} sx={{ p: 2 }}>
    <Typography variant="h6" fontWeight={700}>Free-text intent routes</Typography>
    <Typography variant="body2" color="text.secondary">Used only when AI Intent Detection is enabled and deterministic handling cannot process the message.</Typography>
    {intents.map((intent, index) => <Paper key={index} variant="outlined" sx={{ p: 1.5 }}><Stack spacing={1}>
      <Stack direction="row" spacing={1}><TextField fullWidth size="small" label="Intent name" value={intent.name} onChange={(event) => onChange(intents.map((item, position) => position === index ? { ...item, name: event.target.value.toUpperCase().replace(/\s+/g, "_") } : item))} /><IconButton onClick={() => onChange(intents.filter((_, position) => position !== index))}><Delete /></IconButton></Stack>
      <TextField size="small" label="Example phrases (one per line)" multiline value={intent.examples.join("\n")} onChange={(event) => onChange(intents.map((item, position) => position === index ? { ...item, examples: event.target.value.split("\n").filter(Boolean) } : item))} />
      <NodeTargetSelect nodes={nodes} value={intent.targetNodeId} label="Destination" onChange={(targetNodeId) => onChange(intents.map((item, position) => position === index ? { ...item, targetNodeId } : item))} />
    </Stack></Paper>)}
    <Button startIcon={<Add />} onClick={() => onChange([...intents, { name: "NEW_INTENT", examples: [], targetNodeId: "" }])}>Add intent</Button>
  </Stack>;
}

function WhatsAppPreview({ open, flow, onClose }: { open: boolean; flow: FlowDocument | null; onClose: () => void }) {
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const send = useCallback(async (message?: Record<string, any>) => {
    if (!flow) return;
    setLoading(true); setError("");
    try {
      const response = await flowApi.preview(flow.definition, preview?.state || {}, message);
      setPreview((current) => ({
        state: response.state,
        events: [...(current?.events || []), ...response.events],
        messages: [...(current?.messages || []), ...response.messages],
      }));
    } catch (reason) { setError(messageFor(reason)); }
    finally { setLoading(false); }
  }, [flow, preview?.state]);

  useEffect(() => {
    if (open) { setPreview(null); setInput(""); setError(""); void send(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, flow?.flowId, flow?.version]);

  const clickOption = (interactiveType: string, id: string, title: string) => {
    setPreview((current) => current ? { ...current, messages: [...current.messages, { type: "customer", text: title }] } : current);
    void send({ type: "interactive", interactive: interactiveType === "button" ? { button_reply: { id } } : { list_reply: { id } } });
  };

  return <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>WhatsApp Preview</DialogTitle>
    <DialogContent sx={{ bgcolor: "#efeae2", minHeight: 480 }}>
      {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
      <Stack spacing={1.5} sx={{ py: 2 }}>
        {(preview?.messages || []).map((item, index) => {
          if (item.type === "customer") return <Box key={index} alignSelf="flex-end" sx={{ bgcolor: "#d9fdd3", borderRadius: 2, px: 1.5, py: 1, maxWidth: "85%" }}>{item.text}</Box>;
          if (item.type === "text") return <Box key={index} alignSelf="flex-start" sx={{ bgcolor: "white", borderRadius: 2, px: 1.5, py: 1, maxWidth: "85%", whiteSpace: "pre-wrap" }}>{item.text?.body}</Box>;
          const interactive = item.interactive || {};
          const buttons = interactive.action?.buttons || [];
          const rows = interactive.action?.sections?.flatMap((section: any) => section.rows || []) || [];
          return <Paper key={index} sx={{ alignSelf: "flex-start", p: 1.5, maxWidth: "90%" }}>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{interactive.body?.text}</Typography>
            <Stack spacing={0.5} mt={1}>{buttons.map((button: any) => <Button key={button.reply.id} onClick={() => clickOption("button", button.reply.id, button.reply.title)}>{button.reply.title}</Button>)}{rows.map((row: any) => <Button key={row.id} onClick={() => clickOption("list", row.id, row.title)}>{row.title}</Button>)}</Stack>
          </Paper>;
        })}
        {loading && <CircularProgress size={22} />}
      </Stack>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <TextField fullWidth size="small" placeholder="Type a deterministic input" value={input} onChange={(event) => setInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" && input.trim()) { const text = input.trim(); setInput(""); setPreview((current) => current ? { ...current, messages: [...current.messages, { type: "customer", text }] } : current); void send({ type: "text", text: { body: text } }); } }} />
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>;
}

function FlowBuilderContent() {
  const { businessId: routeBusinessId } = useParams<{ businessId?: string }>();
  const { user } = useAuthUser();
  const { isSuperAdmin } = useSuperAdminAccess();
  const navigate = useNavigate();
  const [businessId, setBusinessId] = useState(routeBusinessId || "");
  const [flows, setFlows] = useState<FlowDocument[]>([]);
  const [flow, setFlow] = useState<FlowDocument | null>(null);
  const [configuration, setConfiguration] = useState<ChatbotConfiguration | null>(null);
  const [connectors, setConnectors] = useState<FlowConnector[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationIssues, setValidationIssues] = useState<FlowValidationIssue[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    const resolve = async () => {
      if (routeBusinessId) { setBusinessId(routeBusinessId); return; }
      if (!user?.uid) return;
      try {
        const settings = await getUserSettingsById(user.uid);
        setBusinessId(settings.businessSettings?.businessId || "");
      } catch (reason) { setError(messageFor(reason)); }
    };
    void resolve();
  }, [routeBusinessId, user?.uid]);

  const load = useCallback(async () => {
    if (!businessId) return;
    setLoading(true); setError("");
    try {
      const [flowItems, config, connectorItems] = await Promise.all([
        flowApi.list(businessId), flowApi.configuration(businessId), flowApi.connectors(businessId),
      ]);
      setFlows(flowItems);
      setConfiguration(config);
      setConnectors(connectorItems);
      setFlow((current) => current ? flowItems.find((item) => item.flowId === current.flowId && item.version === current.version) || flowItems[0] || null : flowItems[0] || null);
    } catch (reason) { setError(messageFor(reason)); }
    finally { setLoading(false); }
  }, [businessId]);

  useEffect(() => { void load(); }, [load]);

  const updateDefinition = (definition: FlowDefinition) => setFlow((current) => current ? { ...current, definition } : current);
  const updateNode = (nextNode: FlowNodeDefinition) => {
    if (!flow) return;
    updateDefinition({ ...flow.definition, nodes: flow.definition.nodes.map((node) => node.id === nextNode.id ? nextNode : node) });
  };
  const selectedNode = flow?.definition.nodes.find((node) => node.id === selectedNodeId) || null;

  const canvasNodes: Node[] = useMemo(() => (flow?.definition.nodes || []).map((node) => ({
    id: node.id,
    position: node.position || { x: 80, y: 80 },
    data: { label: <Box sx={{ textAlign: "left" }}><Typography variant="caption" color="text.secondary">{node.type}</Typography><Typography fontWeight={700}>{node.name}</Typography>{flow?.definition.startNodeId === node.id && <Chip size="small" color="success" label="Start" sx={{ mt: 0.5 }} />}</Box> },
    style: { borderLeft: `5px solid ${nodeColor(node.type)}`, borderRadius: 8, minWidth: 170, boxShadow: selectedNodeId === node.id ? `0 0 0 2px ${nodeColor(node.type)}` : undefined },
  })), [flow, selectedNodeId]);

  const canvasEdges: Edge[] = useMemo(() => (flow?.definition.nodes || []).flatMap((node) => targetsFor(node).filter((target) => target.id).map((target, index) => ({
    id: `${node.id}-${target.id}-${index}`, source: node.id, target: target.id, label: target.label, animated: node.type === "CONDITION", type: "smoothstep",
  }))), [flow]);

  const addNode = (type: FlowNodeType) => {
    if (!flow) return;
    const id = newId(type);
    const base: FlowNodeDefinition = { id, type, name: NODE_TYPES.find((item) => item.type === type)?.label || type, position: { x: 180 + flow.definition.nodes.length * 25, y: 100 + flow.definition.nodes.length * 30 } };
    if (["MESSAGE", "MENU", "DYNAMIC_MENU", "INPUT", "HUMAN_HANDOFF", "END"].includes(type)) base.message = "";
    if (type === "MENU") base.options = [];
    if (type === "INPUT") { base.inputKey = "answer"; base.inputType = "text"; base.required = true; }
    if (type === "CONDITION") base.branches = [];
    if (["DYNAMIC_MENU", "CONNECTOR_ACTION"].includes(type)) base.connector = { action: "", parameters: {} };
    if (type === "DYNAMIC_MENU") base.dynamicMapping = { itemsPath: "items", idPath: "id", titlePath: "title", targetNodeId: "" };
    if (type === "HUMAN_HANDOFF") base.handoffReason = "Customer needs assistance";
    updateDefinition({ ...flow.definition, nodes: [...flow.definition.nodes, base] });
    setSelectedNodeId(id); setRightTab(0);
  };

  const connect = (connection: Connection) => {
    if (!flow || !connection.source || !connection.target) return;
    const source = flow.definition.nodes.find((node) => node.id === connection.source);
    if (!source) return;
    if (source.type === "MENU") updateNode({ ...source, options: [...(source.options || []), { id: newId("option"), title: "New option", targetNodeId: connection.target }] });
    else if (source.type === "CONDITION") updateNode({ ...source, branches: [...(source.branches || []), { variable: "variables.value", operator: "eq", value: "", targetNodeId: connection.target }] });
    else if (source.type === "DYNAMIC_MENU") updateNode({ ...source, dynamicMapping: { ...(source.dynamicMapping || { itemsPath: "items", idPath: "id", titlePath: "title" }), targetNodeId: connection.target } });
    else updateNode({ ...source, nextNodeId: connection.target });
  };

  const createFlow = async () => {
    if (!businessId) return;
    setSaving(true); setError("");
    try { const created = await flowApi.create(businessId, `New chatbot flow ${flows.length + 1}`, starterDefinition()); setFlows((current) => [created, ...current]); setFlow(created); setSuccess("Draft flow created."); }
    catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  const save = async () => {
    if (!flow || flow.status !== "DRAFT") return;
    setSaving(true); setError(""); setSuccess("");
    try { const saved = await flowApi.update(businessId, flow); setFlow(saved); setFlows((current) => current.map((item) => item._id === saved._id ? saved : item)); setSuccess("Draft saved."); }
    catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  const validate = async () => {
    if (!flow) return false;
    setError("");
    try { const result = await flowApi.validate(businessId, flow); setValidationIssues(result.issues); setRightTab(2); if (result.valid) setSuccess("Flow validation passed."); return result.valid; }
    catch (reason) { setError(messageFor(reason)); return false; }
  };

  const publish = async () => {
    if (!flow || flow.status !== "DRAFT") return;
    setSaving(true); setError(""); setSuccess("");
    try {
      const saved = await flowApi.update(businessId, flow);
      const published = await flowApi.publish(businessId, saved);
      setFlow(published); setSuccess(`Version ${published.version} published.`); await load();
    } catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  const newVersion = async () => {
    if (!flow) return;
    setSaving(true);
    try { const created = await flowApi.newVersion(businessId, flow); setFlow(created); setFlows((current) => [created, ...current]); setSuccess(`Draft version ${created.version} created.`); }
    catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  const clone = async () => {
    if (!flow) return;
    setSaving(true);
    try { const created = await flowApi.clone(businessId, flow); setFlow(created); setFlows((current) => [created, ...current]); setSuccess("Flow cloned as a new draft."); }
    catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  const createTemplate = async () => {
    if (!flow) return;
    setSaving(true); setError("");
    try { await flowApi.createTemplate(flow.name, flow.definition); setSuccess("Reusable template created for super admins."); }
    catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  const saveConfiguration = async (next: ChatbotConfiguration) => {
    setSaving(true); setError("");
    try { const saved = await flowApi.updateConfiguration(businessId, next); setConfiguration(saved); setSuccess(saved.type === "FLOW" ? "Flow-Based chatbot activated." : "Agentic AI remains active."); }
    catch (reason) { setError(messageFor(reason)); }
    finally { setSaving(false); }
  };

  if (!businessId && !loading) return <Box p={4}><Alert severity="warning">This account has no WhatsApp business ID configured.</Alert></Box>;

  return <Box sx={{ minHeight: "calc(100dvh - 64px)", bgcolor: "background.default", p: { xs: 2, md: 3 }, minWidth: 0 }}>
    <Stack direction={{ xs: "column", lg: "row" }} justifyContent="space-between" gap={2} mb={2}>
      <Box sx={{ flex: 1 }}><DashboardHeading title="Chatbot builder" icon={<AccountTree />} description="Build WhatsApp conversations, connect actions and test each flow before publishing." /><Button size="small" startIcon={<ArrowBack />} onClick={() => navigate(routeBusinessId ? "/super-admin" : "/settings")}>Back to {routeBusinessId ? "business accounts" : "settings"}</Button></Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button startIcon={<Refresh />} onClick={() => void load()}>Refresh</Button>
        <Button startIcon={<PlayArrow />} disabled={!flow} onClick={() => setPreviewOpen(true)}>Preview</Button>
        <Button startIcon={<CheckCircle />} disabled={!flow} onClick={() => void validate()}>Validate</Button>
        <Button startIcon={<ContentCopy />} disabled={!flow || saving} onClick={() => void clone()}>Clone</Button>
        {isSuperAdmin && <Button startIcon={<AccountTree />} disabled={!flow || saving} onClick={() => void createTemplate()}>Save as template</Button>}
        {flow?.status === "PUBLISHED" && <Button variant="outlined" startIcon={<Add />} disabled={saving} onClick={() => void newVersion()}>New version</Button>}
        <Button variant="outlined" startIcon={<Save />} disabled={!flow || flow.status !== "DRAFT" || saving} onClick={() => void save()}>Save draft</Button>
        <Button variant="contained" startIcon={<Publish />} disabled={!flow || flow.status !== "DRAFT" || saving} onClick={() => void publish()}>Publish</Button>
      </Stack>
    </Stack>
    {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
    {success && <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 1 }}>{success}</Alert>}

    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} flexWrap="wrap" useFlexGap alignItems={{ md: "center" }}>
        <TextField select size="small" label="Flow and version" value={flow ? `${flow.flowId}:${flow.version}` : ""} onChange={(event) => { const [flowId, version] = event.target.value.split(":"); setFlow(flows.find((item) => item.flowId === flowId && item.version === Number(version)) || null); setSelectedNodeId(null); }} sx={{ minWidth: { xs: 0, md: 220 }, width: { xs: "100%", md: "auto" } }}>
          {flows.map((item) => <MenuItem key={item._id} value={`${item.flowId}:${item.version}`}>{item.name} · v{item.version} · {item.status}</MenuItem>)}
        </TextField>
        <Button startIcon={<Add />} onClick={() => void createFlow()} disabled={saving}>Create flow</Button>
        {flow && <><TextField size="small" label="Flow name" value={flow.name} disabled={flow.status !== "DRAFT"} onChange={(event) => setFlow({ ...flow, name: event.target.value })} /><Chip label={`v${flow.version} ${flow.status}`} color={flow.status === "PUBLISHED" ? "success" : flow.status === "DRAFT" ? "warning" : "default"} /></>}
        {flow?.status === "PUBLISHED" && configuration && <Button size="small" variant={configuration.activeFlowId === flow.flowId && configuration.activeFlowVersion === flow.version && configuration.type === "FLOW" ? "outlined" : "contained"} disabled={saving || (configuration.activeFlowId === flow.flowId && configuration.activeFlowVersion === flow.version && configuration.type === "FLOW")} onClick={() => void saveConfiguration({ ...configuration, type: "FLOW", activeFlowId: flow.flowId, activeFlowVersion: flow.version })}>{configuration.activeFlowId === flow.flowId && configuration.activeFlowVersion === flow.version && configuration.type === "FLOW" ? "Active flow" : "Use this flow"}</Button>}
        <Box flex={1} />
        {configuration && <Stack direction="row" alignItems="center" spacing={1}>
          <Select size="small" value={configuration.type} onChange={(event) => void saveConfiguration({ ...configuration, type: event.target.value as ChatbotConfiguration["type"], ...(event.target.value === "FLOW" && flow?.status === "PUBLISHED" ? { activeFlowId: flow.flowId, activeFlowVersion: flow.version } : {}) })}>
            <MenuItem value="AGENTIC_AI">Agentic AI</MenuItem><MenuItem value="FLOW" disabled={!flow || flow.status !== "PUBLISHED"}>Flow-Based</MenuItem>
          </Select>
          {configuration.type === "FLOW" && <><FormControlLabel control={<Switch checked={configuration.aiIntentDetectionEnabled} onChange={(_, checked) => void saveConfiguration({ ...configuration, aiIntentDetectionEnabled: checked })} />} label="AI Intent Detection" /><TextField size="small" type="number" label="Confidence" value={configuration.intentConfidenceThreshold} inputProps={{ min: 0, max: 1, step: 0.05 }} onChange={(event) => setConfiguration({ ...configuration, intentConfidenceThreshold: Number(event.target.value) })} onBlur={() => void saveConfiguration(configuration)} sx={{ width: 120 }} /></>}
        </Stack>}
      </Stack>
    </Paper>

    {loading ? <Box py={10} textAlign="center"><CircularProgress /></Box> : !flow ? <Card><CardContent sx={{ py: 8, textAlign: "center" }}><AccountTree sx={{ fontSize: 60, color: "text.disabled" }} /><Typography variant="h6">Create your first chatbot flow</Typography><Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }} onClick={() => void createFlow()}>Create flow</Button></CardContent></Card> : <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "190px minmax(0, 1fr) 300px" }, gap: 1.5, height: { lg: "max(520px, calc(100dvh - 300px))" } }}>
      <Paper variant="outlined" sx={{ p: 1.5, overflow: "auto" }}><Typography fontWeight={700} mb={1}>Components</Typography><Stack spacing={1}>{NODE_TYPES.map((item) => <Button key={item.type} variant="outlined" startIcon={item.icon} disabled={flow.status !== "DRAFT"} onClick={() => addNode(item.type)} sx={{ justifyContent: "flex-start", borderColor: item.color, color: item.color }}>{item.label}</Button>)}</Stack><Divider sx={{ my: 2 }} /><Typography variant="body2" color="text.secondary">Click a component to add it, then connect nodes on the canvas or choose destinations in Configuration.</Typography></Paper>
      <Paper variant="outlined" sx={{ minHeight: 520, overflow: "hidden" }}>
        <ReactFlow nodes={canvasNodes} edges={canvasEdges} onPaneClick={() => setSelectedNodeId(null)} onNodeClick={(_, node) => { setSelectedNodeId(node.id); setRightTab(0); }} onNodeDragStop={(_, dragged) => { const source = flow.definition.nodes.find((item) => item.id === dragged.id); if (source && flow.status === "DRAFT") updateNode({ ...source, position: dragged.position }); }} onConnect={connect} fitView nodesDraggable={flow.status === "DRAFT"} nodesConnectable={flow.status === "DRAFT"}>
          <MiniMap nodeColor={(node) => nodeColor(flow.definition.nodes.find((item) => item.id === node.id)?.type || "MESSAGE")} /><Controls /><Background gap={20} />
        </ReactFlow>
      </Paper>
      <Paper variant="outlined" sx={{ overflow: "auto" }}>
        <Tabs value={rightTab} onChange={(_, value) => setRightTab(value)} variant="fullWidth"><Tab label="Node" /><Tab label="Intents" /><Tab label={`Validation${validationIssues.length ? ` (${validationIssues.length})` : ""}`} /></Tabs>
        {rightTab === 0 && (selectedNode ? <Box component="fieldset" disabled={flow.status !== "DRAFT"} sx={{ border: 0, p: 0, m: 0 }}><NodeEditor node={selectedNode} nodes={flow.definition.nodes.filter((node) => node.id !== selectedNode.id)} connectors={connectors} onChange={updateNode} onDelete={() => { if (flow.definition.startNodeId === selectedNode.id) { setError("Choose another start node before deleting this node."); return; } updateDefinition({ ...flow.definition, nodes: flow.definition.nodes.filter((node) => node.id !== selectedNode.id) }); setSelectedNodeId(null); }} /></Box> : <Stack p={3} spacing={2}><Typography color="text.secondary">Select a node to configure it.</Typography><TextField select size="small" label="Start node" value={flow.definition.startNodeId} disabled={flow.status !== "DRAFT"} onChange={(event) => updateDefinition({ ...flow.definition, startNodeId: event.target.value })}>{flow.definition.nodes.map((node) => <MenuItem key={node.id} value={node.id}>{node.name}</MenuItem>)}</TextField><TextField multiline minRows={3} label="Low confidence fallback" value={flow.definition.fallbackMessage} disabled={flow.status !== "DRAFT"} onChange={(event) => updateDefinition({ ...flow.definition, fallbackMessage: event.target.value })} /></Stack>)}
        {rightTab === 1 && <Box component="fieldset" disabled={flow.status !== "DRAFT"} sx={{ border: 0, p: 0, m: 0 }}><IntentsEditor intents={flow.definition.intents || []} nodes={flow.definition.nodes} onChange={(intents) => updateDefinition({ ...flow.definition, intents })} /></Box>}
        {rightTab === 2 && <Stack p={2} spacing={1}>{validationIssues.length === 0 ? <Alert severity="info">Run validation before publishing.</Alert> : validationIssues.map((issue, index) => <Alert key={index} severity={issue.severity === "ERROR" ? "error" : "warning"} onClick={() => { if (issue.nodeId) { setSelectedNodeId(issue.nodeId); setRightTab(0); } }} sx={{ cursor: issue.nodeId ? "pointer" : "default" }}><strong>{issue.code}</strong><br />{issue.message}</Alert>)}</Stack>}
      </Paper>
    </Box>}
    <WhatsAppPreview open={previewOpen} flow={flow} onClose={() => setPreviewOpen(false)} />
  </Box>;
}

export default function FlowBuilderPage() {
  return <ReactFlowProvider><FlowBuilderContent /></ReactFlowProvider>;
}
